import { useEffect } from 'react';
import { Box, VStack, Spinner, HStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { ArticleProgressOptions, WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { statisticsApi } from '@texas/api/endpoints/statisticsApi';
import { ProgressChart } from './ProgressChart';
import { StatisticsHeaderFilters } from '../shared/StatisticsHeaderFilters';
import { useWidgetStorage } from '../shared/useWidgetStorage';

interface Props extends BaseWidgetProps {
  options?: ArticleProgressOptions;
}

export function ArticleProgressWidget({
  color,
  customTitle,
  widgetId,
  options,
}: Props) {
  const { t } = useTranslation();
  const { filters, updateSampleSize, updateCustomerId, updateBranchIds } =
    useWidgetStorage(widgetId);

  const { data, loading, refetch } = useApiResource(
    statisticsApi.getArticleProgress,
  );

  const selectedCustomerId = options?.customerId ?? filters.customerId;
  const selectedBranchIds =
    options?.branchIds && options.branchIds.length > 0
      ? options.branchIds
      : filters.branchIds;

  useEffect(() => {
    refetch({
      sampleSize: filters.sampleSize,
      customerId: selectedCustomerId ?? undefined,
      branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : undefined,
    });
  }, [filters.sampleSize, selectedCustomerId, selectedBranchIds, refetch]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      customColor={color}
      type={WidgetType.ArticleProgress}
      style="default"
      header={customTitle ?? t('widgets.articleProgress.title')}
      toolbox={
        <HStack spacing={2} overflowX="auto">
          <StatisticsHeaderFilters
            sampleSize={filters.sampleSize}
            onSampleSizeChange={updateSampleSize}
            selectedCustomerId={selectedCustomerId}
            onCustomerChange={updateCustomerId}
            selectedBranchIds={selectedBranchIds}
            onBranchChange={updateBranchIds}
            isCustomerLocked={!!options?.customerId}
            isBranchLocked={
              !!options?.branchIds && options.branchIds.length > 0
            }
            sampleSizeLabel={t('widgets.articleProgress.articleSizeLabel')}
          />
        </HStack>
      }
    >
      <VStack align="stretch" spacing={4} height="100%">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <Spinner />
          </Box>
        ) : (
          <ProgressChart
            data={data?.stages ?? []}
            earliestCreatedDate={data?.earliestCreatedDate}
            branchStages={data?.branchStages ?? []}
          />
        )}
      </VStack>
    </WidgetWrapper>
  );
}
